import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import SearchIcon from '@material-ui/icons/Search';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { spassuCodeApiRef } from '../../api';
import type { AuditLogEntry, AuditLogFilters } from '../../api/types';

const ACTION_LABELS: Record<string, string> = {
  'member.add': 'Membro adicionado',
  'member.remove': 'Membro removido',
  'member.update': 'Role atualizado',
  'phase.approve': 'Fase aprovada',
  'phase.reject': 'Fase rejeitada',
  'phase.interact': 'Interação com fase',
  'activity.create': 'Activity criada',
  'activity.delete': 'Activity excluída',
  'artifact.approve': 'Artefato aprovado',
  'artifact.reject': 'Artefato rejeitado',
};

const useStyles = makeStyles(theme => ({
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  detailRow: {
    backgroundColor: theme.palette.background.default,
  },
  jsonPre: {
    fontSize: '0.75rem',
    maxHeight: 200,
    overflow: 'auto',
    margin: 0,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  stateLabel: {
    fontWeight: 600,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
}));

export interface AuditLogPanelProps {
  projectKey?: string;
}

export const AuditLogPanel = ({ projectKey }: AuditLogPanelProps = {}) => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    limit: 20,
    projectKey,
  });
  const [searchInput, setSearchInput] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  useEffect(() => {
    setFilters(f => (f.projectKey === projectKey ? f : { ...f, projectKey, page: 1 }));
  }, [projectKey]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters(f => {
        const term = searchInput.trim();
        if ((f.search ?? '') === term) return f;
        return { ...f, search: term || undefined, page: 1 };
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const {
    value: result,
    loading,
    error,
  } = useAsync(() => api.getAuditLog(filters), [filters]);

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setFilters(f => ({ ...f, page: newPage + 1 }));
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilters(f => ({ ...f, limit: parseInt(e.target.value, 10), page: 1 }));
    },
    [],
  );

  const toggleRow = useCallback((id: string) => {
    setExpandedRow(prev => (prev === id ? null : id));
  }, []);

  const formatAction = (action: string) => ACTION_LABELS[action] ?? action;

  const formatEntityRef = (ref: string) => {
    const parts = ref.split('/');
    return parts[parts.length - 1] ?? ref;
  };

  const hasDetails = (entry: AuditLogEntry) =>
    entry.beforeState !== null || entry.afterState !== null;

  if (error) {
    return (
      <Typography color="error">
        Erro ao carregar audit log: {error.message}
      </Typography>
    );
  }

  return (
    <div>
      <div className={classes.filters}>
        <TextField
          label="Pesquisar"
          placeholder="Ator, ação, projeto ou recurso"
          size="small"
          variant="outlined"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          style={{ minWidth: 280, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
          <InputLabel>Ação</InputLabel>
          <Select
            value={filters.action ?? ''}
            onChange={e =>
              setFilters(f => ({
                ...f,
                action: (e.target.value as string) || undefined,
                page: 1,
              }))
            }
            label="Ação"
          >
            <MenuItem value="">Todas</MenuItem>
            {Object.entries(ACTION_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="De"
          type="date"
          size="small"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          value={filters.fromDate ?? ''}
          onChange={e =>
            setFilters(f => ({
              ...f,
              fromDate: e.target.value || undefined,
              page: 1,
            }))
          }
        />
        <TextField
          label="Até"
          type="date"
          size="small"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          value={filters.toDate ?? ''}
          onChange={e =>
            setFilters(f => ({
              ...f,
              toDate: e.target.value || undefined,
              page: 1,
            }))
          }
        />
      </div>

      {loading && <Typography>Carregando...</Typography>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={40} />
            <TableCell>Data</TableCell>
            <TableCell>Ator</TableCell>
            <TableCell>Ação</TableCell>
            <TableCell>Projeto</TableCell>
            <TableCell>Recurso</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {result?.data.map((entry: AuditLogEntry) => (
            <Fragment key={entry.id}>
              <TableRow hover>
                <TableCell>
                  {hasDetails(entry) && (
                    <Tooltip title="Ver detalhes">
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(entry.id)}
                      >
                        {expandedRow === entry.id ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {new Date(entry.createdAt).toLocaleString('pt-BR')}
                </TableCell>
                <TableCell>{formatEntityRef(entry.actorRef)}</TableCell>
                <TableCell>{formatAction(entry.action)}</TableCell>
                <TableCell>{entry.projectKey ?? '—'}</TableCell>
                <TableCell>
                  {entry.resourceType
                    ? `${entry.resourceType}/${entry.resourceId ?? ''}`
                    : '—'}
                </TableCell>
              </TableRow>
              {hasDetails(entry) && (
                <TableRow
                  key={`${entry.id}-detail`}
                  className={classes.detailRow}
                >
                  <TableCell
                    colSpan={6}
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                  >
                    <Collapse
                      in={expandedRow === entry.id}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box margin={1} display="flex" style={{ gap: 16 }}>
                        {entry.beforeState && (
                          <div style={{ flex: 1 }}>
                            <Typography className={classes.stateLabel}>
                              Antes
                            </Typography>
                            <pre className={classes.jsonPre}>
                              {JSON.stringify(entry.beforeState, null, 2)}
                            </pre>
                          </div>
                        )}
                        {entry.afterState && (
                          <div style={{ flex: 1 }}>
                            <Typography className={classes.stateLabel}>
                              Depois
                            </Typography>
                            <pre className={classes.jsonPre}>
                              {JSON.stringify(entry.afterState, null, 2)}
                            </pre>
                          </div>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          ))}
          {!loading && (!result || result.data.length === 0) && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body2" color="textSecondary">
                  Nenhum registro encontrado.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {result && result.total > 0 && (
        <TablePagination
          component="div"
          count={result.total}
          page={(result.page ?? 1) - 1}
          rowsPerPage={result.limit ?? 20}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="Por pagina:"
        />
      )}
    </div>
  );
};
